import { useEffect, useMemo, useRef, useState } from 'react';
import { Pencil, Archive, Trash2, AlertTriangle, Check, X } from 'lucide-react';
import type { TutorialFile } from '@platform/shared-types';
import { videoTasksApi, type LibraryItem, type MeterRow } from '../../services/videoTasksApi';
import { tutorialApi, tutorialCdnUrl, detectStage } from '../../services/tutorialApi';
import { api } from '../../services/apiService';
import type { Tenant } from '../../types';
import { ConfirmDialog } from '../../components/finance/financeUi';
import { RubyLoader } from '../../components/RubyLoader';
import { RubyGemMark } from '../../components/RubyGemMark';
import { STATUS_LABEL, STATUS_TONE, SURFACE_LABEL, mmss } from './videoTaskUi';
import { formatBytes, groupByRecorderTenant, liveFiles, storageStats } from './libraryMath';

// Video library (SA-Library, Board 4): every tutorial video on the platform,
// grouped by the tenant that recorded it, with where each file lives and
// what it weighs. Delete (purge) is irreversible, so it sits behind retire
// AND a type-DELETE dialog.

const REGION = 'us-west-1';

const FILE_KIND_LABEL: Record<TutorialFile['kind'], string> = {
  raw: 'Raw recording',
  transcript: 'Transcript',
  script: 'Cleaned script',
  mp4: 'Rendered video',
  poster: 'Poster',
  vtt: 'Chapters (VTT)',
  mic: 'Voice audio',
};

const pill = 'inline-flex w-fit text-[11px] font-bold px-2.5 py-1 rounded-full';
const iconBtn = 'w-11 h-11 rounded-lg border flex items-center justify-center transition disabled:opacity-40 disabled:cursor-not-allowed';
const inputCls = 'w-full min-h-[44px] rounded-[10px] border border-gray-300 px-3 text-sm text-gray-900 focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/15 focus:outline-none';

const isPurged = (it: LibraryItem) => !!it.tutorial.purgedAt;

/** Status column: purged -> Deleted; retired -> Retired; an open task's status; else the tutorial state. */
function statusOf(it: LibraryItem): { label: string; tone: string } {
  if (isPurged(it)) return { label: 'Deleted', tone: 'bg-red-50 text-red-600' };
  const s = it.tutorial.state;
  if (s === 'retired') return { label: 'Retired', tone: 'bg-gray-100 text-gray-700' };
  if (it.assignment && it.assignment.status !== 'approved') {
    return { label: STATUS_LABEL[it.assignment.status], tone: STATUS_TONE[it.assignment.status] };
  }
  if (s === 'published') return { label: 'Published', tone: 'bg-emerald-100 text-emerald-700' };
  return { label: 'Draft', tone: 'bg-gray-100 text-gray-500' };
}

/** Length from the live version's render, else the newest version that has one. */
function durationOf(it: LibraryItem): number | null {
  const live = it.versions.find((v) => v.versionNo === it.tutorial.publishedVersionNo);
  if (live?.render?.durationMs) return live.render.durationMs;
  const rendered = [...it.versions].sort((a, b) => b.versionNo - a.versionNo).find((v) => v.render?.durationMs);
  return rendered?.render.durationMs ?? null;
}

const liveFileCount = (it: LibraryItem) => it.versions.reduce((n, v) => n + liveFiles(v).length, 0);

const shortDate = (ms?: number) =>
  (ms ? new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—');

export default function LibraryTab() {
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [meters, setMeters] = useState<MeterRow[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [groupKey, setGroupKey] = useState<string | null>(null);
  const [rowId, setRowId] = useState<string | null>(null);

  const [editId, setEditId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editBusy, setEditBusy] = useState(false);

  const [retireId, setRetireId] = useState<string | null>(null);
  const [retireBusy, setRetireBusy] = useState(false);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteText, setDeleteText] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  /** Synchronous in-flight guard: purge is irreversible, so a second Enter/click must never start another. */
  const purging = useRef(false);
  const [deleteError, setDeleteError] = useState('');
  /** tutorialId -> files that could not be deleted on the last purge. */
  const [purgeFailed, setPurgeFailed] = useState<Record<string, number>>({});

  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  /** Reloads library + meters. `quiet` keeps the current page on screen. */
  const load = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const [lib, st] = await Promise.all([videoTasksApi.library(), videoTasksApi.storage()]);
      setItems(lib.items ?? []);
      setMeters(st.meters ?? []);
      setError('');
    } catch (e: any) {
      setError(e?.message || 'Could not load the library.');
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // Names only -- the page works with ids if this fails.
    api.tenants.getAll().then((t) => setTenants(Array.isArray(t) ? t : [])).catch(() => setTenants([]));
  }, []);

  const tenantName = (id: string) => {
    const t = tenants.find((x) => x.id === id);
    return t?.name || t?.businessName || id;
  };

  const groups = useMemo(
    () => groupByRecorderTenant(items).map((g) => (g.key === 'GLOBAL' ? g : { ...g, label: tenantName(g.key) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, tenants],
  );
  const maxBytes = Math.max(1, ...groups.map((g) => g.bytes));
  const group = groups.find((g) => g.key === groupKey) ?? groups[0];
  const rows = group?.items ?? [];
  const row = rows.find((r) => r.tutorial.tutorialId === rowId) ?? rows[0];
  const stats = storageStats(meters);
  const stage = detectStage();

  const itemById = (id: string | null) => items.find((x) => x.tutorial.tutorialId === id);

  const startEdit = (it: LibraryItem) => {
    setEditId(it.tutorial.tutorialId);
    setEditTitle(it.tutorial.title);
    setEditTags((it.tutorial.tags ?? []).join(', '));
  };

  const saveEdit = async () => {
    if (!editId) return;
    setEditBusy(true);
    try {
      const tags = editTags.split(',').map((s) => s.trim()).filter(Boolean);
      const { tutorial } = await videoTasksApi.updateMeta(editId, { title: editTitle.trim() || undefined, tags });
      setItems((prev) => prev.map((x) => (x.tutorial.tutorialId === editId ? { ...x, tutorial } : x)));
      setEditId(null);
      setError('');
    } catch (e: any) {
      setError(e?.message || 'Could not save the changes.');
    } finally {
      setEditBusy(false);
    }
  };

  const runRetire = async () => {
    if (!retireId) return;
    setRetireBusy(true);
    try {
      const { tutorial } = await tutorialApi.retire(retireId);
      setItems((prev) => prev.map((x) => (x.tutorial.tutorialId === retireId ? { ...x, tutorial } : x)));
      setRetireId(null);
      setError('');
    } catch (e: any) {
      setError(e?.message || 'Could not retire the video.');
    } finally {
      setRetireBusy(false);
    }
  };

  const openDelete = (id: string) => { setDeleteId(id); setDeleteText(''); setDeleteError(''); };
  const closeDelete = () => { if (!deleteBusy) setDeleteId(null); };

  const runDelete = async () => {
    if (deleteBusy || purging.current) return;
    if (!deleteId || deleteText !== 'DELETE') return;
    purging.current = true;
    setDeleteBusy(true);
    setDeleteError('');
    try {
      const { failed } = await videoTasksApi.purge(deleteId);
      setPurgeFailed((m) => {
        const { [deleteId]: _drop, ...rest } = m;
        return failed.length ? { ...rest, [deleteId]: failed.length } : rest;
      });
      setDeleteId(null);
      // Refetch: per-file deletedAt, totals and meters all moved server-side.
      await load(true);
    } catch (e: any) {
      setDeleteError(e?.message || 'Delete did not run. Nothing was removed.');
    } finally {
      purging.current = false;
      setDeleteBusy(false);
    }
  };

  const copyLink = async (key: string) => {
    try {
      await navigator.clipboard.writeText(tutorialCdnUrl(key));
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
    } catch {
      setError('Could not copy to the clipboard.');
    }
  };

  if (loading) return <RubyLoader variant="section" label="Loading the library…" />;

  const deleteItem = itemById(deleteId);
  const retireItem = itemById(retireId);

  const tiles = [
    { label: 'Videos', value: String(items.length), sub: 'all tenants + RubyTill global' },
    { label: 'Storage used', value: formatBytes(stats.usedBytes), sub: 'raw + rendered + audio' },
    { label: 'Tenants storing video', value: String(stats.recorderTenants), sub: 'tenant-own library comes later' },
    { label: 'Added this month', value: formatBytes(stats.addedThisMonth), sub: 'growth, for future storage fees' },
  ];

  return (
    <div className="flex flex-col gap-5">
      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
          {error}
          <button type="button" onClick={() => setError('')} aria-label="Dismiss" className="w-11 h-11 -my-2 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </p>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {tiles.map((s) => (
          <div key={s.label} className="border border-gray-200 rounded-xl px-4 py-3.5 flex flex-col gap-1">
            <span className="text-xs text-gray-500">{s.label}</span>
            <span className="text-2xl font-bold text-gray-900">{s.value}</span>
            <span className="text-[11.5px] text-gray-500">{s.sub}</span>
          </div>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="bg-gray-50 rounded-2xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          No videos yet. Recorded tutorials and video tasks show up here.
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[320px_1fr] items-start">
          {/* By tenant */}
          <div className="flex flex-col gap-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">By tenant</span>
            {groups.map((g) => {
              const on = g.key === group?.key;
              const recorders = new Set(g.items.map((x) => x.tutorial.recordedBy?.userId).filter(Boolean)).size;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => { setGroupKey(g.key); setRowId(null); setEditId(null); }}
                  aria-pressed={on}
                  className={`text-left rounded-xl px-3.5 py-3 flex flex-col gap-2 transition ${
                    on ? 'border-2 border-[#DC143C] bg-[#FFF1F3]' : 'border border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <span className="flex justify-between gap-2 text-[13.5px]">
                    <span className="font-semibold text-gray-900 truncate">{g.label}</span>
                    <span className="text-gray-700 flex-shrink-0">{formatBytes(g.bytes)}</span>
                  </span>
                  <span className="h-1.5 rounded-full bg-gray-100 relative overflow-hidden">
                    <span className="absolute inset-y-0 left-0 rounded-full bg-[#DC143C]" style={{ width: `${(g.bytes / maxBytes) * 100}%` }} />
                  </span>
                  <span className="text-[11.5px] text-gray-500">
                    {g.items.length} video{g.items.length === 1 ? '' : 's'}
                    {g.key === 'GLOBAL' ? ' · RubyTill-authored' : ` · ${recorders} recorder${recorders === 1 ? '' : 's'}`}
                  </span>
                </button>
              );
            })}
            <span className="text-[11.5px] text-gray-500 leading-relaxed mt-1">
              Sizes are recorded at upload — exact, and cheap to total. Metered only; storage fees come later.
            </span>
          </div>

          <div className="flex flex-col gap-3 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-base font-bold text-gray-900">{group?.label}</span>
              <span className="text-xs text-gray-500">
                {rows.length} video{rows.length === 1 ? '' : 's'} · {formatBytes(group?.bytes ?? 0)}
              </span>
            </div>

            <div className="border border-gray-200 rounded-[14px] overflow-x-auto">
              <div className="min-w-[860px]">
                <div className="grid grid-cols-[2.2fr_1.3fr_0.9fr_0.7fr_0.8fr_0.9fr_1.2fr] gap-2.5 px-3.5 py-2.5 bg-gray-50 text-[11px] font-bold text-gray-500 uppercase tracking-wide">
                  <span>Video</span><span>Recorded by</span><span>App</span><span>Length</span><span>Size</span><span>Status</span>
                  <span className="text-right">Actions</span>
                </div>
                {rows.map((it) => {
                  const t = it.tutorial;
                  const id = t.tutorialId;
                  const on = id === row?.tutorial.tutorialId;
                  const st = statusOf(it);
                  const dur = durationOf(it);
                  const canDelete = t.state === 'retired' && !isPurged(it);
                  return (
                    <div key={id} className={`border-t border-gray-100 ${on ? 'bg-[#FFF1F3]' : ''}`}>
                      <div
                        className="grid grid-cols-[2.2fr_1.3fr_0.9fr_0.7fr_0.8fr_0.9fr_1.2fr] gap-2.5 px-3.5 min-h-[56px] items-center text-[13px] cursor-pointer"
                        onClick={() => setRowId(id)}
                      >
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <span className="font-semibold text-gray-900 truncate">{t.title}</span>
                          <span className="text-[11.5px] text-gray-500 truncate">
                            {t.slug}{t.tags?.length ? ` · ${t.tags.join(', ')}` : ''}
                          </span>
                        </div>
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <span className="truncate">{t.recordedBy?.name || (t.recordedBy ? t.recordedBy.userId : 'RubyTill')}</span>
                          <span className="text-[11.5px] text-gray-500">{shortDate(t.createdAt)}</span>
                        </div>
                        <span className="text-[12.5px]">{SURFACE_LABEL[t.surface] ?? t.surface}</span>
                        <span className="text-[12.5px]">{dur ? mmss(dur) : '—'}</span>
                        <span className="text-[12.5px] font-semibold">{it.totalBytes ? formatBytes(it.totalBytes) : '—'}</span>
                        <span className={`${pill} ${st.tone}`}>{st.label}</span>
                        <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            aria-label="Edit"
                            title="Edit title and tags"
                            disabled={isPurged(it)}
                            onClick={() => { setRowId(id); startEdit(it); }}
                            className={`${iconBtn} border-gray-200 bg-white hover:bg-gray-50`}
                          >
                            <Pencil className="w-[15px] h-[15px] text-gray-700" />
                          </button>
                          <button
                            type="button"
                            aria-label="Retire"
                            title={t.state === 'retired' ? 'Already retired' : 'Retire'}
                            disabled={t.state === 'retired'}
                            onClick={() => setRetireId(id)}
                            className={`${iconBtn} border-gray-200 bg-white hover:bg-gray-50`}
                          >
                            <Archive className="w-[15px] h-[15px] text-gray-700" />
                          </button>
                          <span className="relative group">
                            <button
                              type="button"
                              aria-label={canDelete ? 'Delete' : isPurged(it) ? 'Already deleted' : 'Delete — retire it first'}
                              disabled={!canDelete}
                              onClick={() => openDelete(id)}
                              className={`${iconBtn} border-rose-200 bg-[#FFF1F3] hover:bg-rose-100`}
                            >
                              <Trash2 className="w-[15px] h-[15px] text-[#DC143C]" />
                            </button>
                            {!canDelete && (
                              <span
                                role="tooltip"
                                className="pointer-events-none absolute right-0 bottom-full mb-1.5 whitespace-nowrap rounded-md bg-gray-900 text-white text-[11px] font-semibold px-2 py-1 opacity-0 group-hover:opacity-100 transition"
                              >
                                {isPurged(it) ? 'Already deleted' : 'Retire it first'}
                              </span>
                            )}
                          </span>
                        </div>
                      </div>

                      {editId === id && (
                        <div className="px-3.5 pb-3.5 grid gap-3 md:grid-cols-[1fr_1fr_auto] items-end">
                          <label className="block">
                            <span className="block text-[12.5px] font-semibold text-gray-700 mb-1.5">Title</span>
                            <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className={inputCls} />
                          </label>
                          <label className="block">
                            <span className="block text-[12.5px] font-semibold text-gray-700 mb-1.5">Tags (comma-separated)</span>
                            <input value={editTags} onChange={(e) => setEditTags(e.target.value)} placeholder="e.g. stock, grn" className={inputCls} />
                          </label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setEditId(null)}
                              className="min-h-[44px] px-4 rounded-[10px] border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => void saveEdit()}
                              disabled={editBusy || !editTitle.trim()}
                              className="min-h-[44px] px-4 rounded-[10px] bg-[#DC143C] text-white text-sm font-semibold hover:bg-[#B01030] disabled:opacity-40 inline-flex items-center gap-2"
                            >
                              {editBusy ? <RubyGemMark size={14} drawing /> : <Check className="w-4 h-4" />}
                              Save
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {row && purgeFailed[row.tutorial.tutorialId] ? (
              <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  {purgeFailed[row.tutorial.tutorialId]} file{purgeFailed[row.tutorial.tutorialId] === 1 ? '' : 's'} could
                  not be deleted — run Delete again.
                </span>
              </div>
            ) : null}

            {row && (
              <div className="border border-gray-200 rounded-[14px] px-4 py-3.5 flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-sm font-bold text-gray-900">Where it’s stored — {row.tutorial.title}</span>
                  <span className="text-xs text-gray-500">agenticpos-media-{stage} · {REGION}</span>
                </div>
                {liveFileCount(row) === 0 ? (
                  <p className="text-[12.5px] text-gray-500 py-1">
                    {isPurged(row) ? 'All files deleted. The record stays in the audit log.' : 'No files recorded for this video yet.'}
                  </p>
                ) : (
                  [...row.versions].sort((a, b) => b.versionNo - a.versionNo).flatMap((v) =>
                    liveFiles(v).map((f) => (
                      <div key={`${v.versionNo}:${f.key}`} className="flex items-center gap-3 text-[12.5px] min-h-[44px]">
                        <span className="w-[140px] flex-shrink-0 font-semibold text-gray-900">
                          {FILE_KIND_LABEL[f.kind] ?? f.kind}
                          <span className="font-normal text-gray-400"> · v{v.versionNo}</span>
                        </span>
                        <span className="font-mono text-[11.5px] text-gray-700 flex-1 min-w-0 truncate" title={f.key}>{f.key}</span>
                        <span className="w-[70px] text-right font-semibold flex-shrink-0">{formatBytes(f.sizeBytes)}</span>
                        <button
                          type="button"
                          onClick={() => void copyLink(f.key)}
                          className="w-[84px] min-h-[44px] text-right text-[#DC143C] hover:text-[#9F1239] font-medium flex-shrink-0"
                        >
                          {copiedKey === f.key ? 'Copied' : 'Copy link'}
                        </button>
                      </div>
                    )),
                  )
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Retire */}
      <ConfirmDialog
        open={!!retireId}
        title={`Retire “${retireItem?.tutorial.title ?? ''}”?`}
        tone="danger"
        confirmLabel="Retire"
        busy={retireBusy}
        onConfirm={() => void runRetire()}
        onClose={() => { if (!retireBusy) setRetireId(null); }}
      >
        <p>
          This takes it off the "current" list but deletes nothing — the published video stays reachable at its
          manifest URL. Once retired, it can be deleted from storage here.
        </p>
      </ConfirmDialog>

      {/* Delete (purge) -- irreversible, so type DELETE to confirm. */}
      {deleteItem && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={closeDelete}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="library-delete-title"
            className="bg-white rounded-2xl border border-gray-200 shadow-xl w-full max-w-[400px] p-5 flex flex-col gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="library-delete-title" className="text-base font-bold text-gray-900">
              Delete “{deleteItem.tutorial.title}”?
            </h3>
            <p className="text-[13px] text-gray-700 leading-relaxed">
              {`Removes ${liveFileCount(deleteItem)} file${liveFileCount(deleteItem) === 1 ? '' : 's'} (${formatBytes(deleteItem.totalBytes)}) from storage for good, including the raw voice recording. The record stays in the audit log.`}
            </p>
            <label className="flex flex-col gap-1.5 text-[12.5px] font-semibold text-gray-700">
              Type DELETE to confirm
              <input
                autoFocus
                disabled={deleteBusy}
                value={deleteText}
                onChange={(e) => setDeleteText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && deleteText === 'DELETE') void runDelete(); if (e.key === 'Escape') closeDelete(); }}
                autoComplete="off"
                spellCheck={false}
                className={`${inputCls} font-normal disabled:bg-gray-50`}
              />
            </label>
            {deleteError && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{deleteError}</p>
            )}
            <div className="flex gap-2.5 justify-end">
              <button
                type="button"
                onClick={closeDelete}
                disabled={deleteBusy}
                className="min-h-[44px] px-4 rounded-[10px] border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void runDelete()}
                disabled={deleteText !== 'DELETE' || deleteBusy}
                className="min-h-[44px] px-[18px] rounded-[10px] bg-[#DC143C] text-white text-sm font-semibold hover:bg-[#B01030] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {deleteBusy && <RubyGemMark size={14} drawing />}
                Delete files
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
